import React, { useEffect, useState } from 'react'
import ProtectedRoute from '../../routes/protected'
import SidebarCargosSalarios from '../../components/sidebars/sidebarCargosSalarios'
import salaryAnalysisService from '../../services/data/salary-analysis.service'
import companyService from '../../services/data/company.service'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { Dropdown } from 'primereact/dropdown'

function deviationColor(pct) {
    if (pct <= -20) return { bg: '#1a5276', text: '#fff' }
    if (pct <= -10) return { bg: '#2980b9', text: '#fff' }
    if (pct < 0) return { bg: '#aed6f1', text: '#333' }
    if (pct === 0) return { bg: '#e8f8f5', text: '#333' }
    if (pct <= 10) return { bg: '#f9ebea', text: '#333' }
    if (pct <= 20) return { bg: '#e74c3c', text: '#fff' }
    return { bg: '#7b241c', text: '#fff' }
}

export default function MapaCalor() {
    const [data, setData] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [subcompanySel, setSubcompanySel] = useState(null)
    const [subcompanyItems, setSubcompanyItems] = useState([])

    function loadData(filters = {}) {
        setLoading(true); setError('')
        salaryAnalysisService.getMapaCalor(filters)
            .then(setData)
            .catch(e => setError(JSON.stringify(e?.response?.data || e)))
            .finally(() => setLoading(false))
    }

    useEffect(() => { loadData() }, [])
    useEffect(() => {
        companyService.autoCompleteSubCompany('')
            .then(r => setSubcompanyItems(r || []))
            .catch(() => setSubcompanyItems([]))
    }, [])

    function applyFilters() {
        const f = {}
        if (subcompanySel) f.subcompany_id = subcompanySel.id
        loadData(f)
    }

    // Organizar dados em matriz dept × grade
    const departments = [...new Set(data.map(d => d.department))]
    const grades = [...new Set(data.map(d => d.grade))]

    function getCell(dept, grade) {
        return data.find(d => d.department === dept && d.grade === grade)
    }

    return (
        <ProtectedRoute>
            <div className="container-fluid">
                <div className='row flex-nowrap'>
                    <SidebarCargosSalarios />
                    <div className="col-xs-10 col-sm-9 col-md-9 col-xl-10 p-0 formMarginBottom">
                        <div className='col text-bg-green p-3'>
                            <span className="fs-6 fw-bold">Mapa de Calor Salarial</span>
                        </div>

                        <div className='row m-3 g-2 align-items-end'>
                            <div className='col-md-3'>
                                <label className='form-label small fw-bold'>Organização</label>
                                <Dropdown className='w-100' placeholder='Filtrar por organização'
                                    value={subcompanySel}
                                    options={subcompanyItems}
                                    optionLabel='title'
                                    filter
                                    showClear
                                    onChange={e => setSubcompanySel(e.value)} />
                            </div>
                            <div className='col-auto'>
                                <button className='btn btn-success btn-sm' onClick={applyFilters}>
                                    <FontAwesomeIcon icon={solid('magnifying-glass')} /> Filtrar
                                </button>
                            </div>
                            <div className='col-auto'>
                                <button className='btn btn-link btn-sm text-grey text-decoration-none'
                                    onClick={() => { setSubcompanySel(null); loadData() }}>
                                    Limpar
                                </button>
                            </div>
                        </div>

                        {/* Legenda */}
                        <div className='mx-3 mb-2 d-flex align-items-center gap-2 flex-wrap'>
                            <small className='text-muted'>Desvio vs. Midpoint:</small>
                            {[{label: '≤ -20%', pct: -25}, {label: '-10%', pct: -15}, {label: '-5%', pct: -5},
                              {label: '0%', pct: 0}, {label: '+5%', pct: 5}, {label: '+10%', pct: 15}, {label: '≥ +20%', pct: 25}].map((l, i) => {
                                const c = deviationColor(l.pct)
                                return <span key={i} className='badge px-2' style={{background: c.bg, color: c.text}}>{l.label}</span>
                            })}
                        </div>

                        {error && <div className='alert alert-danger mx-3'>{error}</div>}

                        {loading && (
                            <div className='text-center p-5'>
                                <div className="spinner-border text-success" role="status" />
                            </div>
                        )}

                        {!loading && data.length === 0 && (
                            <p className='text-center text-muted p-5'>
                                Nenhum dado disponível. Verifique se as posições possuem estrutura salarial e colaboradores com salário registrado.
                            </p>
                        )}

                        {!loading && data.length > 0 && (
                            <div className='m-3 table-responsive'>
                                <table className='table table-bordered table-sm'>
                                    <thead>
                                        <tr>
                                            <th className='bg-light'>Departamento</th>
                                            {grades.map((g, i) => (
                                                <th key={i} className='text-center bg-light'>Grau {g}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {departments.map((dept, di) => (
                                            <tr key={di}>
                                                <td className='fw-bold'>{dept}</td>
                                                {grades.map((grade, gi) => {
                                                    const cell = getCell(dept, grade)
                                                    if (!cell) return <td key={gi} className='text-center text-muted'>-</td>
                                                    const colors = deviationColor(cell.avg_deviation_pct)
                                                    return (
                                                        <td key={gi} className='text-center' title={`${dept} / Grau ${grade}`}
                                                            style={{background: colors.bg, color: colors.text, fontWeight: 'bold'}}>
                                                            {cell.avg_deviation_pct > 0 ? '+' : ''}{cell.avg_deviation_pct}%
                                                        </td>
                                                    )
                                                })}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <small className='text-muted'>Valores representam o desvio médio (%) do salário real vs. midpoint da faixa.</small>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </ProtectedRoute>
    )
}
